import React, { useState } from "react";
import {
  TextInput as RNTextInput,
  StyleSheet,
  Platform,
  type KeyboardTypeOptions
} from "react-native";
import { colors, typography, layout, webScrollbarStyles } from "@/constants";

/**
 * Props for the TextInput component
 */
interface TextInputProps {
  /** Current value of the input field */
  value: string;
  /** Placeholder text to show when input is empty */
  placeholder?: string;
  /** Maximum number of characters allowed */
  maxLength?: number;
  /** Custom style to override default styling */
  style?: object;
  /** Callback when text changes */
  onChangeText: (text: string) => void;
  /** Callback when submit/return is pressed */
  onSubmitEditing?: () => void;
  /** Callback when input gains focus */
  onFocus?: () => void;
  /** Callback when input loses focus */
  onBlur?: () => void;
  /** Whether the input supports multiple lines */
  multiline?: boolean;
  /** Whether text entry is obscured (for passwords) */
  secureTextEntry?: boolean;
  /** Keyboard type hint */
  keyboardType?: KeyboardTypeOptions;
  /** Auto-capitalization behavior */
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  /** Auto-complete hint */
  autoComplete?: string;
  /** Whether auto-correct is enabled */
  autoCorrect?: boolean;
  /** Whether the input is editable; when false, applies disabled styling */
  editable?: boolean;
}

/** Minimum visible lines for multiline inputs */
const MULTILINE_MIN_LINES = 3;
const lineHeight = typography.fontSizes.medium * typography.lineHeights.normal;
const multilineMinHeight =
  lineHeight * MULTILINE_MIN_LINES + layout.space.md * 2;

/**
 * Unified text input component with consistent styling and focus behavior.
 * Supports single-line (default) and multiline variants.
 *
 * @param props - The component props
 * @returns A styled TextInput with focus border and design-system styling
 */
export default function TextInput({
  value,
  placeholder = "Enter text...",
  maxLength,
  style,
  onChangeText,
  onSubmitEditing,
  onFocus,
  onBlur,
  multiline = false,
  secureTextEntry,
  keyboardType,
  autoCapitalize,
  autoComplete,
  autoCorrect,
  editable = true
}: TextInputProps) {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  const webScrollbarStyle =
    Platform.OS === "web" && multiline
      ? {
          ...webScrollbarStyles,
          paddingRight: layout.space.xl
        }
      : {};

  const inputStyle = [
    styles.base,
    multiline ? styles.multiline : styles.singleLine,
    {
      borderColor: isFocused ? colors.primary : colors.outlineVariant
    },
    !editable && styles.disabled,
    Platform.OS === "web" && { outlineStyle: "none" as const },
    webScrollbarStyle,
    style
  ];

  return (
    <RNTextInput
      style={inputStyle}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={colors.onSurfaceMuted}
      multiline={multiline}
      maxLength={maxLength}
      onSubmitEditing={onSubmitEditing}
      onFocus={handleFocus}
      onBlur={handleBlur}
      secureTextEntry={secureTextEntry}
      keyboardType={keyboardType}
      autoCapitalize={autoCapitalize}
      autoComplete={autoComplete as any}
      autoCorrect={autoCorrect}
      editable={editable}
    />
  );
}

const styles = StyleSheet.create({
  base: {
    borderWidth: layout.borderWidth.thin,
    borderRadius: layout.borderRadius.medium,
    backgroundColor: colors.surfaceContainer,
    color: colors.onSurface,
    fontSize: typography.fontSizes.medium,
    fontFamily: typography.fontFamily.regular,
    paddingHorizontal: layout.space.lg,
    paddingVertical: layout.space.md
  },
  singleLine: {
    height: layout.size.xl + layout.space.sm
  },
  multiline: {
    minHeight: multilineMinHeight,
    textAlignVertical: "top"
  },
  disabled: {
    opacity: 0.6
  }
});
